import React, { useEffect, useState } from 'react';
import {
  isNativePhotoVault,
  peekCachedImage,
  resolveCachedImage,
  warmProductImage,
} from '../utils/imageCache';

function offlineNow() {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

/**
 * Web: <img src> normal (sin fetch→blob ni disco).
 * APK: prioridad foto en disco; online descarga y guarda.
 */
export default function CachedProductImage({
  src,
  alt = '',
  className,
  style,
  loading = 'eager',
  decoding = 'async',
  fetchPriority = 'auto',
  draggable = false,
  onLoad,
  onError,
}) {
  const native = isNativePhotoVault();

  const [displaySrc, setDisplaySrc] = useState(() => {
    if (!native) return src || null;
    const peeked = peekCachedImage(src);
    if (peeked) return peeked;
    if (src && !offlineNow()) return src;
    return null;
  });

  useEffect(() => {
    if (!native) {
      setDisplaySrc(src || null);
      return undefined;
    }

    let cancelled = false;
    const peeked = peekCachedImage(src);

    if (peeked) setDisplaySrc(peeked);
    else if (src && !offlineNow()) setDisplaySrc(src);
    else setDisplaySrc(null);

    if (!src) return undefined;

    if (!peeked) warmProductImage(src);

    resolveCachedImage(src).then((local) => {
      if (cancelled || !local) return;
      if (local.startsWith('blob:') || local.startsWith('data:')) {
        setDisplaySrc(local);
      } else if (!offlineNow()) {
        setDisplaySrc(local);
      }
    });

    const onOnline = () => {
      if (!src || cancelled) return;
      setDisplaySrc((prev) => prev || src);
      resolveCachedImage(src).then((local) => {
        if (!cancelled && local) setDisplaySrc(local);
      });
    };
    window.addEventListener('online', onOnline);
    return () => {
      cancelled = true;
      window.removeEventListener('online', onOnline);
    };
  }, [src, native]);

  if (!displaySrc) {
    return <div className={className} style={style} aria-hidden="true" />;
  }

  return (
    <img
      src={displaySrc}
      alt={alt}
      className={className}
      style={style}
      loading={loading}
      decoding={decoding}
      fetchPriority={fetchPriority}
      draggable={draggable}
      onLoad={onLoad}
      onError={(e) => {
        if (native && !offlineNow() && src) {
          resolveCachedImage(src).then((local) => {
            if (local && local !== displaySrc) setDisplaySrc(local);
            else onError?.(e);
          });
          return;
        }
        onError?.(e);
      }}
    />
  );
}
